import {
  ActionIcon,
  Button,
  Group,
  MultiSelect,
  Text,
  TextInput,
  Textarea,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendarPlus,
  IconCheck,
  IconClock,
  IconUser,
  IconVideo,
  IconX,
  IconVideoOff,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";
import { createCalendarEvent } from "api";
import MeetLogo from "assets/meet_logo.png";
import { useUser } from "stores/useUser";
import { useAssistantWidgetStatus } from "stores/useAssistantWidgetStatus";
import {
  EMAIL_PATTERN,
  getTeamOptions,
  normalizeEmail,
  notify,
  uniqueEmails,
} from "utils/common";
import {
  formatTimeRange,
  fromLocalInputValue,
  resolveWidgetTimezone,
  toLocalInputValue,
} from "./calendarWidgetTime";
import {
  descriptionHtmlToText,
  descriptionToHtml,
} from "./calendarWidgetDescription";
import useContactDirectory from "hooks/useContactDirectory";
import {
  createDirectoryOptions,
  filterRecipient,
  RecipientOption,
} from "./recipientPicker";
import css from "./MicChatDrawer.module.scss";

const CONFERENCE_PROVIDERS = {
  ZOOM: "zoom",
  GOOGLE_MEET: "google_meet",
  NONE: "none",
};

const getProviderOptions = (isZoomConnected) => [
  {
    value: CONFERENCE_PROVIDERS.ZOOM,
    label: "Zoom",
    icon: (
      <span className={css.calendarDraftZoomIcon}>
        <IconVideo size={13} stroke={2.5} />
      </span>
    ),
    disabled: !isZoomConnected,
    disabledReason:
      "Connect your Zoom account from Integrations first to enable Zoom meetings.",
  },
  {
    value: CONFERENCE_PROVIDERS.GOOGLE_MEET,
    label: "Meet",
    icon: <img src={MeetLogo} alt="" width={14} height={14} />,
  },
  {
    value: CONFERENCE_PROVIDERS.NONE,
    label: "None",
    icon: <IconVideoOff size={15} color="#667085" />,
  },
];

const normalizeAttendeeOptions = (attendees) =>
  attendees
    .map((attendee) => ({
      value: attendee.email,
      label: attendee.displayName || attendee.email,
    }))
    .filter((attendee) => attendee.value);

const mergeOptions = (...optionLists) => {
  const seen = new Set();
  return optionLists.flat().filter((option) => {
    const value = typeof option?.value === "string"
      ? option.value.trim().toLowerCase()
      : "";
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const getUnresolvedAttendeeInput = (attendee) =>
  normalizeEmail(attendee?.email || attendee?.input);

const buildEventPayload = ({ draft, user, tz }) => {
  const userEmail = normalizeEmail(user?.email);
  const attendeeEmails = uniqueEmails(draft.attendeeEmails).filter(
    (email) => email !== userEmail,
  );

  return {
    event: {
      summary: draft.title,
      description: descriptionToHtml(draft.description),
      start: {
        dateTime: draft.startDateTimeIso,
        timeZone: tz,
      },
      end: {
        dateTime: draft.endDateTimeIso,
        timeZone: tz,
      },
      attendees: [
        {
          email: user.email,
          displayName: user.displayName,
          responseStatus: "accepted",
        },
        ...attendeeEmails.map((email) => ({ email })),
      ],
    },
    conferenceProvider: draft.conferenceProvider,
  };
};

const CalendarEventDraftWidget = ({
  draft,
  statusKey,
  portalZIndex,
}) => {
  const { user } = useUser();
  const persistedStatus = useAssistantWidgetStatus(
    (state) => state.statusByKey[statusKey],
  );
  const markStatus = useAssistantWidgetStatus((state) => state.markStatus);
  const clearStatus = useAssistantWidgetStatus((state) => state.clearStatus);
  const tz = resolveWidgetTimezone(draft.timezone);
  const [title, setTitle] = useState(draft.title || "Untitled meeting");
  const [description, setDescription] = useState(
    descriptionHtmlToText(draft.description || ""),
  );
  const [startDateTimeIso, setStartDateTimeIso] = useState(
    draft.startDateTimeIso,
  );
  const [endDateTimeIso, setEndDateTimeIso] = useState(draft.endDateTimeIso);
  const [attendeeEmails, setAttendeeEmails] = useState(
    uniqueEmails(draft.attendees.map((attendee) => attendee.email)),
  );
  const [conferenceProvider, setConferenceProvider] = useState(
    draft.conferenceProvider || CONFERENCE_PROVIDERS.GOOGLE_MEET,
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(persistedStatus === "confirmed");
  const isZoomConnected = Boolean(
    user?.zoomConnection?.state === "connected" && user?.zoomConnection?.id,
  );

  const directory = useContactDirectory();
  const options = useMemo(
    () =>
      mergeOptions(
        normalizeAttendeeOptions(draft.attendees),
        createDirectoryOptions(directory),
        getTeamOptions(),
        attendeeEmails.map((email) => ({ value: email, label: email })),
      ),
    [attendeeEmails, directory, draft.attendees],
  );

  useEffect(() => {
    if (conferenceProvider === CONFERENCE_PROVIDERS.ZOOM && !isZoomConnected) {
      setConferenceProvider(CONFERENCE_PROVIDERS.GOOGLE_MEET);
    }
  }, [conferenceProvider, isZoomConnected]);

  const originalAttendeeEmails = new Set(
    uniqueEmails(draft.attendees.map((attendee) => attendee.email)),
  );
  const addedAttendeeCount = uniqueEmails(attendeeEmails).filter(
    (email) => !originalAttendeeEmails.has(email),
  ).length;
  const exactUnresolvedAttendees = [
    ...draft.unresolvedAttendees,
    ...draft.ambiguousAttendees,
  ].filter((attendee) => {
    const unresolvedInput = getUnresolvedAttendeeInput(attendee);
    return (
      !unresolvedInput ||
      !attendeeEmails.some((email) => normalizeEmail(email) === unresolvedInput)
    );
  });
  const unresolvedAttendees = exactUnresolvedAttendees.slice(
    addedAttendeeCount,
  );
  const unresolvedCount = unresolvedAttendees.length;
  const hasValidTimes = Boolean(
    fromLocalInputValue(toLocalInputValue(startDateTimeIso, tz), tz) &&
      fromLocalInputValue(toLocalInputValue(endDateTimeIso, tz), tz) &&
      new Date(endDateTimeIso).getTime() > new Date(startDateTimeIso).getTime(),
  );
  const canConfirm =
    !isConfirmed &&
    !isConfirming &&
    title.trim() &&
    hasValidTimes &&
    unresolvedCount === 0;

  const statusCopy = useMemo(() => {
    if (unresolvedCount > 0) return "Needs attendee details";
    if (!draft.requestedSlotAvailable) return "Requested time is busy";
    return "Ready to confirm";
  }, [draft.requestedSlotAvailable, unresolvedCount]);

  const handleSelectSuggestion = (slot) => {
    setStartDateTimeIso(slot.startDateTimeIso);
    setEndDateTimeIso(slot.endDateTimeIso);
  };

  const handleCreate = async () => {
    if (!canConfirm) return;

    setIsConfirming(true);
    try {
      await createCalendarEvent(
        buildEventPayload({
          draft: {
            title: title.trim(),
            description,
            startDateTimeIso,
            endDateTimeIso,
            attendeeEmails,
            conferenceProvider,
          },
          user,
          tz,
        }),
      );
      setIsConfirmed(true);
      markStatus(statusKey, "confirmed");
      notify("Event created successfully!", "success");
    } catch (error) {
      notify(error.message || "Failed to create event", "error");
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className={css.calendarDraftCard}>
      <Group position="apart" align="flex-start" spacing="sm" noWrap>
        <div className={css.calendarDraftTitleGroup}>
          <span
            className={`${css.calendarDraftStatusIcon} ${
              draft.requestedSlotAvailable && unresolvedCount === 0
                ? css.calendarDraftStatusReady
                : css.calendarDraftStatusNeedsWork
            }`}
          >
            {draft.requestedSlotAvailable && unresolvedCount === 0 ? (
              <IconCheck size={14} />
            ) : (
              <IconAlertTriangle size={14} />
            )}
          </span>
          <div>
            <Text weight={800} size="sm" color="#253047">
              Calendar draft
            </Text>
            <Text size="xs" color="#667085">
              {statusCopy}
            </Text>
          </div>
        </div>
        {isConfirmed && <span className={css.calendarDraftPill}>Created</span>}
      </Group>

      <div className={css.calendarDraftFields}>
        <TextInput
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftInput,
            label: css.calendarDraftLabel,
          }}
        />

        <div className={css.calendarDraftTimeGrid}>
          <label className={css.calendarDraftNativeField}>
            <span>Start</span>
            <input
              type="datetime-local"
              value={toLocalInputValue(startDateTimeIso, tz)}
              onChange={(event) => {
                const nextIso = fromLocalInputValue(
                  event.currentTarget.value,
                  tz,
                );
                if (nextIso) setStartDateTimeIso(nextIso);
              }}
            />
          </label>
          <label className={css.calendarDraftNativeField}>
            <span>End</span>
            <input
              type="datetime-local"
              value={toLocalInputValue(endDateTimeIso, tz)}
              onChange={(event) => {
                const nextIso = fromLocalInputValue(
                  event.currentTarget.value,
                  tz,
                );
                if (nextIso) setEndDateTimeIso(nextIso);
              }}
            />
          </label>
        </div>

        <MultiSelect
          zIndex={portalZIndex}
          label="Attendees"
          data={options}
          value={attendeeEmails}
          onChange={(emails) => setAttendeeEmails(uniqueEmails(emails))}
          searchable
          creatable
          filter={filterRecipient}
          itemComponent={RecipientOption}
          limit={8}
          getCreateLabel={(query) =>
            EMAIL_PATTERN.test(query) ? `Add ${query}` : "Enter an email"
          }
          onCreate={(query) => {
            const value = query.trim().toLowerCase();
            if (!EMAIL_PATTERN.test(value)) return null;
            return { value, label: value };
          }}
          placeholder="Add people"
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftInput,
            label: css.calendarDraftLabel,
          }}
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          placeholder="Add agenda or notes"
          minRows={3}
          autosize
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftTextarea,
            label: css.calendarDraftLabel,
          }}
        />

        <div>
          <Text className={css.calendarDraftLabel}>Conference</Text>
          <div
            className={css.calendarDraftProvider}
            role="radiogroup"
            aria-label="Conference provider"
          >
            {getProviderOptions(isZoomConnected).map((option) => {
              const isSelected = conferenceProvider === option.value;
              const optionButton = (
                <button
                  key={option.value}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  disabled={option.disabled}
                  className={`${css.calendarDraftProviderOption} ${
                    isSelected ? css.calendarDraftProviderOptionActive : ""
                  }`}
                  onClick={() => setConferenceProvider(option.value)}
                >
                  {option.icon}
                  <span>{option.label}</span>
                </button>
              );

              // A disabled <button> swallows hover events, so wrap it in a span
              // (which still receives hover) and put the Tooltip on that.
              if (option.disabled && option.disabledReason) {
                return (
                  <Tooltip
                    zIndex={portalZIndex}
                    key={option.value}
                    label={option.disabledReason}
                    multiline
                    width={220}
                    withArrow
                    position="top"
                    events={{ hover: true, focus: true, touch: true }}
                  >
                    <span className={css.calendarDraftProviderTooltipTarget}>
                      {optionButton}
                    </span>
                  </Tooltip>
                );
              }

              return optionButton;
            })}
          </div>
        </div>
      </div>

      {!hasValidTimes && (
        <Text size="xs" color="red" className={css.calendarDraftNotice}>
          Choose an end time after the start time.
        </Text>
      )}

      {unresolvedCount > 0 && (
        <div className={css.calendarDraftNotice}>
          {unresolvedAttendees.map((attendee) => (
            <div key={attendee.input} className={css.calendarDraftWarningRow}>
              <IconUser size={13} />
              <span>
                {attendee.input}:{" "}
                {attendee.candidates?.length
                  ? "pick the exact person or add their email"
                  : "email needed"}
              </span>
            </div>
          ))}
        </div>
      )}

      {draft.conflicts.length > 0 && (
        <div className={css.calendarDraftNotice}>
          {draft.conflicts.slice(0, 3).map((conflict) => (
            <div
              key={`${conflict.email}-${conflict.startDateTimeIso}`}
              className={css.calendarDraftWarningRow}
            >
              <IconClock size={13} />
              <span>
                {conflict.displayName || conflict.email} is busy{" "}
                {formatTimeRange(conflict, tz)}
              </span>
            </div>
          ))}
        </div>
      )}

      {draft.suggestedSlots.length > 0 && (
        <div className={css.calendarDraftSuggestions}>
          <Text className={css.calendarDraftLabel}>Suggested slots</Text>
          <div className={css.calendarDraftSuggestionList}>
            {draft.suggestedSlots.map((slot) => {
              const isActive =
                slot.startDateTimeIso === startDateTimeIso &&
                slot.endDateTimeIso === endDateTimeIso;
              return (
                <button
                  type="button"
                  key={`${slot.startDateTimeIso}-${slot.endDateTimeIso}`}
                  className={`${css.calendarDraftSuggestion} ${
                    isActive ? css.calendarDraftSuggestionActive : ""
                  }`}
                  onClick={() => handleSelectSuggestion(slot)}
                >
                  {slot.label && (
                    <span className={css.calendarDraftSuggestionLabel}>
                      {slot.label}
                    </span>
                  )}
                  {formatTimeRange(slot, tz)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <Group position="apart" spacing="xs" className={css.calendarDraftFooter}>
        <Text size="xs" color="#667085" className={css.calendarDraftCurrentTime}>
          {formatTimeRange({ startDateTimeIso, endDateTimeIso }, tz)}
        </Text>
        <Group spacing={6} noWrap>
          {isConfirmed && (
            <ActionIcon
              variant="subtle"
              color="dark"
              onClick={() => {
                setIsConfirmed(false);
                clearStatus(statusKey);
              }}
              title="Create another copy"
            >
              <IconX size={14} />
            </ActionIcon>
          )}
          <Button
            compact
            leftIcon={<IconCalendarPlus size={14} />}
            disabled={!canConfirm}
            loading={isConfirming}
            onClick={handleCreate}
            className={css.calendarDraftConfirm}
          >
            Confirm
          </Button>
        </Group>
      </Group>
    </div>
  );
};

export default CalendarEventDraftWidget;
